import { useEffect, useMemo, useState } from "react";
import { cn } from "@/utils/cn";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLoginUser } from "@/hooks/useAuth";
import { useWantedOverviewQuery } from "@/hooks/roster-wanted/useWantedOverviewQuery";
import type { WantedOverview } from "@/services/wanted/overview";
import { groupSnapshotTeams } from "@/utils/groupSnapshotTeams";
import {
  buildWantedOverviewRows,
  summarizeWantedOverview,
} from "../utils/wantedOverview";

export function WantedOverviewDialog({
  year,
  month,
  initialGroupId,
  onClose,
  nurseRoles,
}: {
  readonly year: number;
  readonly month: number;
  readonly initialGroupId: string;
  readonly onClose: () => void;
  readonly nurseRoles?: readonly {
    readonly nurse_id: string;
    readonly role: string;
  }[];
}) {
  const user = useLoginUser();
  const rolesByNurseId = useMemo(
    () => new Map(nurseRoles?.map((nurse) => [nurse.nurse_id, nurse.role])),
    [nurseRoles]
  );
  const [groupId, setGroupId] = useState(initialGroupId);
  const [teamView, setTeamView] = useState(false);
  const [groups, setGroups] = useState<WantedOverview["my_groups"]>([]);
  const query = useWantedOverviewQuery({ year, month, groupId });
  const data = query.isError ? undefined : query.data;
  useEffect(() => {
    if (query.data && !query.isError) setGroups(query.data.my_groups);
  }, [query.data, query.isError]);
  const rows = useMemo(
    () => (data ? buildWantedOverviewRows(data, user?.nurse_id ?? "") : []),
    [data, user?.nurse_id]
  );
  const summary = useMemo(
    () => (data ? summarizeWantedOverview(data) : null),
    [data]
  );
  const hasTeams = !!data?.teams.length;
  const groupedSections =
    data && teamView && hasTeams
      ? groupSnapshotTeams(data.teams, rows)
      : [{ team_id: null, team_name: "", nurses: rows }];
  const sections = groupedSections.filter(
    (section) => section.nurses.length > 0
  );
  const hasRegisteredNurses = groupedSections.some(
    (section) => section.team_id !== null && section.nurses.length > 0
  );
  const days = Array.from(
    { length: new Date(year, month, 0).getDate() },
    (_, i) => i + 1
  );
  const prefix = `${year}-${String(month).padStart(2, "0")}-`;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[85dvh] w-[calc(100vw-48px)] max-w-[1200px] flex-col font-pretendard"
        data-testid="wanted-overview-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {year}년 {month}월 원티드 요청 현황
          </DialogTitle>
          <DialogDescription>
            근무자의 신청 날짜와 근무코드를 확인합니다.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          {groups.length > 1 && (
            <label className="flex min-w-0 items-center gap-2 text-sm">
              조회 병동
              <select
                aria-label="원티드 현황 조회 병동"
                name="overview-ward"
                value={groupId}
                onChange={(event) => setGroupId(event.target.value)}
                className="max-w-80 rounded-md border border-border bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {!groups.some((group) => group.group_id === groupId) && (
                  <option value={groupId}>현재 조회 병동</option>
                )}
                {groups.map((group) => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name ?? group.group_id}
                    {group.kind === "inbound" ? " (파견)" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          {data && (
            <span className="text-sm text-muted-foreground">
              근무자 {rows.length}명
            </span>
          )}
          {hasTeams && (
            <button
              type="button"
              aria-pressed={teamView}
              className={cn(
                "inline-flex h-7 shrink-0 items-center justify-center rounded-full border px-3 text-xs font-semibold shadow-sm transition-colors whitespace-nowrap",
                teamView
                  ? "border-[#0E59C6] bg-[#EFF6FF] text-[#0E59C6]"
                  : "border-slate-200 bg-white text-slate-600",
                "hover:border-[#0E59C6]/40 hover:bg-[#F5F8FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
              )}
              onClick={() => setTeamView((value) => !value)}
            >
              {teamView ? "전체보기" : "팀별보기"}
            </button>
          )}
        </div>
        {groups.length > 1 && (
          <p className="text-xs text-muted-foreground">
            조회 병동을 바꿔도 내 원티드 작성 병동은 바뀌지 않습니다.
          </p>
        )}
        {query.isError ? (
          <div
            role="alert"
            className="flex items-center justify-center gap-3 py-12"
          >
            <span>원티드 요청 현황을 불러오지 못했습니다.</span>
            <Button variant="outline" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : query.isPending ? (
          <div
            role="status"
            aria-label="원티드 요청 현황을 불러오는 중"
            className="min-h-0 overflow-hidden"
            data-testid="wanted-overview-skeleton"
          >
            <span className="sr-only">원티드 요청 현황을 불러오는 중…</span>
            <div aria-hidden="true" className="space-y-4">
              <Skeleton className="h-5 w-24 bg-light-muted motion-reduce:animate-none" />
              <div className="overflow-hidden rounded-md border border-border">
                {Array.from({ length: 6 }, (_, row) => (
                  <div
                    key={row}
                    className="grid items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                    style={{
                      gridTemplateColumns: `6.5rem repeat(${days.length}, minmax(0, 1fr))`,
                    }}
                  >
                    <Skeleton className="h-5 w-20 bg-light-muted motion-reduce:animate-none" />
                    {days.map((day) => (
                      <Skeleton
                        key={day}
                        className="h-5 w-full bg-light-muted motion-reduce:animate-none"
                      />
                    ))}
                  </div>
                ))}
              </div>
              <Skeleton className="h-5 w-64 max-w-full bg-light-muted motion-reduce:animate-none" />
            </div>
          </div>
        ) : data && summary ? (
          <>
            {summary.total === 0 && (
              <p role="status" className="text-sm text-muted-foreground">
                이 병동에 신청된 원티드가 없습니다.
              </p>
            )}
            <div
              className="min-h-0 overflow-auto overscroll-contain rounded-md border border-border"
              tabIndex={0}
              role="region"
              aria-label="병동 전체 원티드 요청 표"
            >
              <table
                className="w-full table-fixed border-collapse text-center text-sm tabular-nums"
                style={{ minWidth: `${8 + days.length * 1.75}rem` }}
              >
                <thead className="sticky top-0 z-10 bg-light-muted">
                  <tr>
                    <th
                      scope="col"
                      className="sticky left-0 z-20 w-32 bg-light-muted px-3 py-2"
                    >
                      이름
                    </th>
                    {days.map((day) => (
                      <th scope="col" key={day} className="px-1 py-2">
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                {teamView && !hasRegisteredNurses && (
                  <tbody>
                    <tr>
                      <td
                        colSpan={days.length + 1}
                        className="bg-light-muted px-4 py-2 text-left text-muted-foreground"
                      >
                        등록된 팀이 없습니다.
                      </td>
                    </tr>
                  </tbody>
                )}
                {sections.map((section) => (
                  <tbody key={section.team_id ?? "all"}>
                    {section.team_name && (
                      <tr>
                        <th
                          scope="rowgroup"
                          colSpan={days.length + 1}
                          className="bg-light-muted px-4 py-2 text-left"
                        >
                          {section.team_name} · {section.nurses.length}명
                        </th>
                      </tr>
                    )}
                    {section.nurses.map((row) => (
                      <tr
                        key={row.nurse_id}
                        className={`border-t border-border ${row.nurse_id === user?.nurse_id ? "bg-[#f3f6ff]" : ""}`}
                      >
                        <th
                          scope="row"
                          className={`sticky left-0 px-3 py-2 text-left font-medium break-words ${row.nurse_id === user?.nurse_id ? "bg-[#f3f6ff] font-semibold text-sig" : "bg-background"}`}
                        >
                          <span>{row.name}</span>
                          {(row.role?.trim() ||
                            rolesByNurseId.get(row.nurse_id)?.trim()) && (
                            <span className="ml-1.5 inline-block text-xs font-normal text-muted-foreground">
                              {row.role?.trim() ||
                                rolesByNurseId.get(row.nurse_id)?.trim()}
                            </span>
                          )}
                          {row.nurse_id === user?.nurse_id && (
                            <span className="sr-only"> (본인)</span>
                          )}
                        </th>
                        {days.map((day) => (
                          <td
                            key={day}
                            className="px-1 py-2 [overflow-wrap:anywhere]"
                          >
                            {row.byDate.get(
                              `${prefix}${String(day).padStart(2, "0")}`
                            ) ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
            <p
              className="text-sm text-muted-foreground"
              data-testid="wanted-overview-summary"
            >
              병동 전체 신청 {summary.total}건
              {summary.max > 0 &&
                ` · 가장 몰린 날짜 ${summary.busiest.map((date) => `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`).join(", ")} (${summary.max}명)`}
            </p>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
